/* eslint-disable @typescript-eslint/no-explicit-any */
import MiiaFwConfig from '@/configs/config'
import type { ApiExecutionResult } from '@/types/api/common/apiResponse'
import type {
  ExportRequestParam,
  ExportRequestResponse
} from '@/types/api/emergencyResultCsv/exportRequestType'
import type {
  ExportStatusParam,
  ExportStatusResponse
} from '@/types/api/emergencyResultCsv/exportStatusType'
import type {
  DownloadUrlParam,
  DownloadUrlResponse
} from '@/types/api/emergencyResultCsv/downloadUrlType'
import { useApiGw } from '@/features/miiaFw/composables/useApiGw'
import { ERROR_MESSAGES } from '@/constants/error/errorMessage'
import { ERROR_CODES } from '@/constants/error/errorCode'

/**
 * 調査結果一覧CSV APIのexportRequest関数を実行する
 * @param params リクエストパラメータ
 * @returns レスポンス
 */
export const exportRequest = async (
  params: ExportRequestParam
): Promise<ApiExecutionResult<ExportRequestResponse>> => {
  return await callEmergencyResultCsvApi({ function: 'exportRequest', params })
}

/**
 * 調査結果一覧CSV APIのexportStatus関数を実行する
 * @param params リクエストパラメータ
 * @returns レスポンス
 */
export const exportStatus = async (
  params: ExportStatusParam
): Promise<ApiExecutionResult<ExportStatusResponse>> => {
  return await callEmergencyResultCsvApi({ function: 'exportStatus', params })
}

export const downloadUrl = async (
  params: DownloadUrlParam
): Promise<ApiExecutionResult<DownloadUrlResponse>> => {
  return await callEmergencyResultCsvApi({ function: 'downloadUrl', params })
}

type ApiArg =
  | {
      function: 'exportRequest'
      params: ExportRequestParam
    }
  | {
      function: 'exportStatus'
      params: ExportStatusParam
    }
  | {
      function: 'downloadUrl'
      params: DownloadUrlParam
    }
type ApiArgFunction = ApiArg['function']

type ReturnTypeByFunction<F extends ApiArgFunction> = F extends 'exportRequest'
  ? ApiExecutionResult<ExportRequestResponse>
  : F extends 'exportStatus'
    ? ApiExecutionResult<ExportStatusResponse>
    : F extends 'downloadUrl'
      ? ApiExecutionResult<DownloadUrlResponse>
      : never

/**
 * 調査結果一覧CSV APIを実行する。
 * @param apiArg APIパラメータ
 * @returns APIレスポンス
 */
async function callEmergencyResultCsvApi<F extends ApiArgFunction>(
  apiArg: Extract<ApiArg, { function: F }>
): Promise<ReturnTypeByFunction<F>> {
  return await new Promise((resolve, reject) => {
    const { invokeApiGwFunc } = useApiGw()
    invokeApiGwFunc(
      // API Gatewayのエンドポイント
      MiiaFwConfig.config.apiGwEndpointEmergency,
      // Lambda関数をコールするためのWebAPIのサブディレクトリ
      '/emergencyResultCsv',
      // Lambda関数に渡す引数
      { ...apiArg },
      // Lambda関数実行前の前処理のコールバック
      () => {},
      // 成功時のコールバック
      response => resolve(response),
      // 例外受信時のコールバック(引数: エラーメッセージ)
      (message: any) => {
        reject({ message: message })
      },
      // 予期せぬエラー(アクセス回線障害など)発生時のコールバック
      () => {
        reject({ message: ERROR_MESSAGES[ERROR_CODES.API_EXECUTION_FAILED].message })
      },
      // 処理完了後に実行するコールバック
      () => {},
      false
    )
  })
}
